import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { UserOnline } from '../../Types/WebsocketServer';
import { processData } from './Utils/processData';
import { InfoscreenProfileCard } from './Widget/InfoscreenProfileCard';
import WidgetRenderer from './Widget/WidgetRenderer';

import type { Dashboard, infoscreenLayoutView, ProfileListUser, SiteSelect } from '../../Types/AuthServer';

interface InfoscreenLayoutViewProps {
    availableUsers?: ProfileListUser[];
    carouselInterval: number | undefined;
    infoscreenLayout: Dashboard['socials'];
    invalidUserIds?: string[];
    // Optional editing props
    isEditingMode?: boolean;
    layoutViews: infoscreenLayoutView[] | undefined;
    onlineData: UserOnline[] | undefined;
    onRemoveUser?: (row: number, col: number) => void;

    onUserSelect?: (userId: string, row: number, col: number) => void;
    // Custom profile card renderer for editing mode
    renderProfileCard?: (props: {
        col: number;
        isEditingMode?: boolean;
        onlineUserData?: UserOnline;
        row: number;
        userData?: ProfileListUser;
        userId: string | null;
    }) => React.ReactNode;
    showBackgroundForImages?: boolean; // UI-only toggle for showing background behind fullscreen images
    sites: SiteSelect[];
    small?: boolean;

    users: ProfileListUser[] | undefined;
}

export default function InfoscreenLayoutView({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    availableUsers, // Used indirectly via renderProfileCard in editing mode
    carouselInterval = 30,
    infoscreenLayout,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    invalidUserIds, // Used indirectly via renderProfileCard in editing mode
    isEditingMode = false,
    layoutViews,
    onlineData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onRemoveUser, // Used indirectly via renderProfileCard in editing mode
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onUserSelect, // Used indirectly via renderProfileCard in editing mode
    renderProfileCard,
    sites,
    small,
    users,
}: Readonly<InfoscreenLayoutViewProps>) {
    // Carousel state
    const [currentViewIndex, setCurrentViewIndex] = useState(0);

    // Enable carousel only in TV mode with multiple views
    const shouldEnableCarousel = !isEditingMode && carouselInterval > 0 && (layoutViews?.length || 0) > 1;

    // Handle carousel auto-advance
    useEffect(() => {
        if (!shouldEnableCarousel || !layoutViews) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentViewIndex((prevIndex) => (prevIndex + 1) % layoutViews.length);
        }, carouselInterval * 1000);

        return () => clearInterval(interval);
    }, [shouldEnableCarousel, carouselInterval, layoutViews]);

    // Function to render a single view (people/grid/fullscreen widget views only)
    const renderSingleView = (view: infoscreenLayoutView) => {
        const type = view.type;
        const combinedItems = processData(view);

        if (type === 'fullscreen') {
            return (
                <Container
                    disableGutters
                    maxWidth={false}
                    sx={{
                        height: '100%',
                        margin: 'auto',
                        maxWidth: 1538,
                        py: ['2.5vh', '2.5cqh'],
                    }}
                >
                    <Box
                        key={view.id}
                        sx={{
                            height: '100%',
                            pl: isEditingMode ? 0 : 1,
                            width: '100%',
                        }}
                    >
                        <Stack
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                height: '100%',
                                justifyContent: 'center',
                            }}
                        >
                            <WidgetRenderer selectedWidget={view.fullscreen} sites={sites} />
                        </Stack>
                    </Box>
                </Container>
            );
        }

        if (type === 'grid') {
            return (
                <Container
                    disableGutters
                    maxWidth={false}
                    sx={{
                        height: '100%',
                        margin: 'auto',
                        maxWidth: 1538,
                        py: ['2.5vh', '2.5cqh'],
                    }}
                >
                    <Box
                        key={view.id}
                        sx={{
                            height: '100%',
                            pl: isEditingMode ? 0 : 1,
                            width: '100%',
                        }}
                    >
                        <Grid container sx={{ alignItems: 'center', display: 'flex', height: '100%' }}>
                            {combinedItems.map((item) => (
                                <Grid
                                    key={`${view.id.toString()}-${item.slotId}`}
                                    size={2.4 * (item.count || 1)}
                                    sx={{
                                        height: '50%',
                                        position: 'relative',
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            alignItems: 'center',
                                            height: '100%',
                                            justifyContent: 'center',
                                            position: 'relative',
                                        }}
                                    >
                                        <WidgetRenderer
                                            selectedWidget={item.itemId.replace(/-\d+$/, '')}
                                            sites={sites}
                                        />
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            );
        }

        // People view type (default) - shows user profile cards in a 2x5 grid
        if (type === 'people' || !type) {
            // In editing mode, render the exact 2x5 grid structure
            if (isEditingMode && renderProfileCard) {
                return (
                    <Box
                        key={view.id}
                        sx={{
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Grid container sx={{ alignItems: 'center', display: 'flex', height: '100%' }}>
                            {/* Row 1 */}
                            {view.itemsRow1.map((userId, colIndex) => (
                                <Grid
                                    key={`0-${colIndex.toString()}`}
                                    size={2.4}
                                    sx={{
                                        height: '50%',
                                        p: 2,
                                        position: 'relative',
                                    }}
                                >
                                    {renderProfileCard({
                                        col: colIndex,
                                        isEditingMode: true,
                                        onlineUserData: userId
                                            ? onlineData?.find((user) => user.user_id === userId)
                                            : undefined,
                                        row: 0,
                                        userData: userId ? users?.find((user) => user.id === userId) : undefined,
                                        userId: userId || null,
                                    })}
                                </Grid>
                            ))}
                            {/* Row 2 */}
                            {view.itemsRow2.map((userId, colIndex) => (
                                <Grid
                                    key={`1-${colIndex.toString()}`}
                                    size={2.4}
                                    sx={{
                                        height: '50%',
                                        p: 2,
                                        position: 'relative',
                                    }}
                                >
                                    {renderProfileCard({
                                        col: colIndex,
                                        isEditingMode: true,
                                        onlineUserData: userId
                                            ? onlineData?.find((user) => user.user_id === userId)
                                            : undefined,
                                        row: 1,
                                        userData: userId ? users?.find((user) => user.id === userId) : undefined,
                                        userId: userId || null,
                                    })}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            }

            // Normal TV display mode - use the existing logic with processData
            return (
                <Container
                    disableGutters
                    maxWidth={false}
                    sx={{
                        height: '100%',
                        margin: 'auto',
                        maxWidth: 1538,
                        py: ['2.5vh', '2.5cqh'],
                    }}
                >
                    <Box
                        key={view.id}
                        sx={{
                            height: '100%',
                            pl: 1,
                            width: '100%',
                        }}
                    >
                        <Grid
                            container
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                height: '100%',
                                py: '2vh',
                            }}
                        >
                            {combinedItems.map((item) => (
                                <Grid
                                    key={`${view.id.toString()}-${item.slotId}`}
                                    size={2.4 * item.count}
                                    sx={{
                                        height: '50%',
                                        p: 2,
                                        position: 'relative',
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            display: 'flex',
                                            height: '100%',
                                            position: 'relative',
                                        }}
                                    >
                                        {item.itemId ? (
                                            <InfoscreenProfileCard
                                                infoscreenLayout={infoscreenLayout}
                                                onlineUserData={onlineData?.find(
                                                    (user) => user.user_id === item.itemId,
                                                )}
                                                type={type}
                                                userData={users?.find((user) => user.id === item.itemId)}
                                            />
                                        ) : null}
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            );
        }

        // Fallback for unknown view types
        return (
            <Container
                disableGutters
                maxWidth={false}
                sx={{
                    height: '100%',
                    margin: 'auto',
                    maxWidth: 1538,
                    py: ['2.5vh', '2.5cqh'],
                }}
            >
                <Box
                    key={view.id}
                    sx={{
                        height: '100%',
                        pl: isEditingMode ? 0 : 1,
                        width: '100%',
                    }}
                >
                    <Stack
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography color={infoscreenLayout?.textColor || '#ffffff'} variant="h4">
                            Unknown view type: {type}
                        </Typography>
                    </Stack>
                </Box>
            </Container>
        );
    };

    // Helper function for common container pattern
    function createViewContainer({
        children,
        isEditingMode,
        viewId,
    }: {
        children: React.ReactNode;
        isEditingMode: boolean;
        viewId: string | number;
    }) {
        return (
            <Container
                disableGutters
                maxWidth={false}
                sx={{
                    height: '100%',
                    margin: 'auto',
                    maxWidth: 1538,
                    py: ['2.5vh', '2.5cqh'],
                }}
            >
                <Box
                    key={viewId}
                    sx={{
                        height: '100%',
                        pl: isEditingMode ? 0 : 1,
                        width: '100%',
                    }}
                >
                    {children}
                </Box>
            </Container>
        );
    }

    return small ? (
        <Grid
            container
            sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                width: '100%',
            }}
        >
            {layoutViews
                ? processData(layoutViews[0]).map((item, index) => (
                      <Grid
                          key={`${index.toString()}-${item.slotId}`}
                          size={2.4 * item.count}
                          sx={{
                              display: 'flex',
                              height: '50%',
                              p: 1,
                              position: 'relative',
                          }}
                      >
                          <Stack
                              sx={{
                                  display: 'flex',
                                  flex: 1,
                                  height: '100%',
                                  position: 'relative',
                              }}
                          >
                              {item.itemId ? (
                                  <InfoscreenProfileCard
                                      infoscreenLayout={infoscreenLayout}
                                      onlineUserData={onlineData?.find((user) => user.user_id === item.itemId)}
                                      small={small}
                                      type={layoutViews[0]?.type}
                                      userData={users?.find((user) => user.id === item.itemId)}
                                  />
                              ) : null}
                          </Stack>
                      </Grid>
                  ))
                : null}
        </Grid>
    ) : (
        <Container
            disableGutters
            maxWidth={false}
            sx={{
                height: '100%',
                // Fixed container - never changes layout, views handle their own spacing
                maxWidth: '100%',
                py: 0,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    height: '100%',
                    margin: 'auto',
                    width: '100%',
                }}
            >
                {/* In editing mode, render views directly without carousel */}
                {isEditingMode ? (
                    <Box
                        sx={{
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        {/* Show only the first view in editing mode */}
                        {layoutViews?.[0] ? renderSingleView(layoutViews[0]) : null}
                    </Box>
                ) : (
                    /* Fade transition carousel for TV display */
                    <Box
                        sx={{
                            height: '100%',
                            position: 'relative',
                            width: '100%',
                        }}
                    >
                        {layoutViews?.map((view, index) => (
                            <Box
                                key={view.id}
                                sx={{
                                    height: '100%',
                                    left: 0,
                                    opacity: index === currentViewIndex ? 1 : 0,
                                    position: 'absolute',
                                    top: 0,
                                    transition: 'opacity 0.5s ease-in-out',
                                    width: '100%',
                                    zIndex: index === currentViewIndex ? 1 : 0,
                                }}
                            >
                                {renderSingleView(view)}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Container>
    );
}
